import React, { useMemo, useState } from "react";
import { Button, Modal, Form, Spinner, OverlayTrigger, Tooltip } from "react-bootstrap";
import api from "../../../api";
import swal from "sweetalert";
import { FaUpload, FaFilePdf, FaFileArchive, FaDownload } from "react-icons/fa";
import FilePreview from "./FilePreview";
import SignatureModal from "./SignatureModal";
import usePermission from "../../../hooks/usePermission";

const VerifiedCopyActions = ({ 
  id, 
  type, 
  moduleKey: moduleKeyProp,
  onUploadSuccess, 
  hasVerifiedCopy, 
  verifiedCopy,
  systemPdfUrl,
  verifiedPdfUrl,
  mergedPdfUrl,
  canUpload = true,
  requireSignature = false
}) => {
  const { hasPermission } = usePermission();

  const isSignatureRequired = Boolean(requireSignature || String(type).toLowerCase() === "indents");

  const getFilenameFromDisposition = (disposition, fallback) => {
    if (!disposition) return fallback;

    const encodedMatch = disposition.match(/filename\*=UTF-8''([^;]+)/i);
    if (encodedMatch?.[1]) {
      try {
        return decodeURIComponent(encodedMatch[1]);
      } catch (_) {
        return encodedMatch[1];
      }
    }

    const plainMatch = disposition.match(/filename="?([^";]+)"?/i);
    return plainMatch?.[1] || fallback;
  };

  const hasVerifiedFile =
    Boolean(hasVerifiedCopy) ||
    Boolean(verifiedCopy?.filePath) ||
    Boolean(verifiedCopy?.fileUrl);

  const perms = useMemo(() => {
    const rawType = (type || "").toLowerCase();
    const moduleKey =
      (moduleKeyProp || "").toLowerCase() ||
      ({
        // API route key -> permission module key
        billing: "billings",
        mir: "mirs",
        jmr: "jmrs",
        "testing-commissioning": "tcs",
        "purchase-orders": "pos",
        "vendor-work-orders": "vendorwo",
        handover: "handovers",
      }[rawType] ||
        rawType);
    const moduleAliases = {
      billings: ["billings", "billing"],
      billing: ["billings", "billing"],
      vendorwo: ["vendorwo", "vendor-work-orders", "vendor-work-order"],
      "vendor-work-orders": ["vendor-work-orders", "vendor-work-order"],
      "vendor-work-order": ["vendor-work-orders", "vendor-work-order"],
      "contractual-work-orders": ["contractualwo", "contractual-work-orders", "contractual-work-order"],
      "contractual-work-order": ["contractualwo", "contractual-work-orders", "contractual-work-order"],
      deliveries: ["deliveries", "delivery"],
      delivery: ["deliveries", "delivery"],
      mirs: ["mirs", "mir"],
      mir: ["mirs", "mir"],
      jmrs: ["jmrs", "jmr"],
      jmr: ["jmrs", "jmr"],
      pos: ["pos", "purchase-orders", "purchaseorders"],
      "purchase-orders": ["pos", "purchase-orders", "purchaseorders"],
      purchaseorders: ["pos", "purchase-orders", "purchaseorders"],
      tcs: ["tcs", "tc", "testing-commissioning"],
      "testing-commissioning": ["tcs", "tc", "testing-commissioning"],
      handovers: ["handovers", "handover"],
      handover: ["handovers", "handover"],
      "measurement-sheets": ["measurement-sheets", "measurementsheets", "measurement-sheets"],
      measurementsheets: ["measurement-sheets", "measurementsheets"],
    };
    const keys = moduleAliases[moduleKey] || [moduleKey];

    return {
      downloadSystem: keys.map((key) => `verifiedcopy:download-system:${key}`),
      downloadVerified: keys.map((key) => `verifiedcopy:download-verified:${key}`),
      downloadMerged: keys.map((key) => `verifiedcopy:download-merged:${key}`),
      uploadVerified: keys.map((key) => `verifiedcopy:upload:${key}`),
      legacyRead: keys.map((key) => `read:${key}`),
      legacyManage: keys.map((key) => `manage:${key}`),
    };
  }, [type, moduleKeyProp]);

  const hasAnyPermission = (permissions) =>
    permissions.some((permission) => hasPermission(permission));

  const canDownloadSystem =
    hasAnyPermission(perms.downloadSystem) || hasAnyPermission(perms.legacyRead);
  const canDownloadVerified =
    hasAnyPermission(perms.downloadVerified) || hasAnyPermission(perms.legacyRead);
  const canDownloadMerged =
    hasAnyPermission(perms.downloadMerged) || hasAnyPermission(perms.legacyRead);
  const canUploadVerified =
    hasAnyPermission(perms.uploadVerified) ||
    (canUpload && hasAnyPermission(perms.legacyManage));

  const [showUploadModal, setShowUploadModal] = useState(false);
  const [showSignModal, setShowSignModal] = useState(false);
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [downloading, setDownloading] = useState({ system: false, verified: false, merged: false });

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (!selectedFile) {
      setFile(null);
      return;
    }

    const isPdf =
      selectedFile.type === "application/pdf" ||
      selectedFile.name.toLowerCase().endsWith(".pdf");

    if (!isPdf) {
      setFile(null);
      e.target.value = "";
      return swal("Error", "Please select a PDF file only", "error");
    }

    setFile(selectedFile);
  };

  const handleUploadClick = async () => {
    if (!file) {
      return swal("Error", "Please select a file to upload", "error");
    }

    if (isSignatureRequired) {
      setShowUploadModal(false);
      setShowSignModal(true);
    } else {
      await handleDirectUpload();
    }
  };

  const handleDirectUpload = async () => {
    setUploading(true);
    const formData = new FormData();
    formData.append("verifiedCopy", file);

    try {
      const endpoint = `/${type}/${id}/upload-verified`;
      const res = await api.patch(endpoint, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      if (res.data.success) {
        const pageText = res.data?.data?.pageCount
          ? ` (${res.data.data.pageCount} pages)`
          : "";
        swal("Success", `Verified copy uploaded successfully${pageText}`, "success");
        setShowUploadModal(false);
        setFile(null);
        if (onUploadSuccess) await onUploadSuccess();
      }
    } catch (error) {
      console.error("Upload error:", error);
      swal("Error", error.response?.data?.message || "Failed to upload verified copy", "error");
    } finally {
      setUploading(false);
    }
  };

  const handleSaveSignature = async (sigFile) => {
    if (!file) {
      return swal("Error", "No document selected for upload", "error");
    }

    setUploading(true);
    const formData = new FormData();
    formData.append("verifiedCopy", file);
    if (sigFile) {
      formData.append("signature", sigFile);
      formData.append("requestedBySignature", sigFile);
    }

    try {
      const endpoint = `/${type}/${id}/upload-verified`;
      const res = await api.patch(endpoint, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      if (res.data.success) {
        const pageText = res.data?.data?.pageCount
          ? ` (${res.data.data.pageCount} pages)`
          : "";
        swal("Success", `Verified copy & signature uploaded successfully${pageText}`, "success");
        setShowSignModal(false);
        setFile(null);
        if (onUploadSuccess) await onUploadSuccess();
      }
    } catch (error) {
      console.error("Upload error:", error);
      swal("Error", error.response?.data?.message || "Failed to upload verified copy", "error");
    } finally {
      setUploading(false);
    }
  };

  const handleDownload = async (downloadType) => {
    setDownloading(prev => ({ ...prev, [downloadType]: true }));
    try {
      let endpoint = "";
      let filename = "";

      if (downloadType === "system") {
        endpoint = systemPdfUrl || `/${type}/${id}/pdf`;
        filename = `${type.toUpperCase()}-System-${id}.pdf`;
      } else if (downloadType === "verified") {
        endpoint = verifiedPdfUrl || `/${type}/${id}/download-verified`;
        filename = `Verified-${type.toUpperCase()}-${id}.pdf`;
      } else if (downloadType === "merged") {
        endpoint = mergedPdfUrl || `/${type}/${id}/merge-pdf`;
        filename = `Merged-${type.toUpperCase()}-${id}.pdf`;
      }

      const res = await api.get(endpoint, {
        responseType: "blob",
        timeout: 900000,
        params: { _: Date.now() },
        suppressErrorToast: true,
      });

      // Validate: check if backend actually returned a PDF
      const contentType = res.headers?.["content-type"] || "";
      if (!contentType.includes("application/pdf")) {
        const errorText = await res.data.text();
        swal(
          "PDF Generation Failed",
          errorText
            ? `Server error: ${errorText.slice(0, 300)}`
            : "The server could not generate the PDF. Please try again or contact support.",
          "error"
        );
        return;
      }

      const disposition = res.headers?.["content-disposition"];
      filename = getFilenameFromDisposition(disposition, filename);

      if (
        downloadType === "merged" &&
        !String(filename).toLowerCase().startsWith("merged-")
      ) {
        filename = `Merged-${filename}`;
      }

      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const link = document.createElement("a");
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.setTimeout(() => window.URL.revokeObjectURL(url), 3000);
    } catch (error) {
      console.error("Download error:", error);
      // Try to extract error message from blob response
      let errMsg = "Failed to download file";
      if (error.response?.data instanceof Blob) {
        try {
          const text = await error.response.data.text();
          if (text) errMsg = text;
        } catch (_) {}
      } else if (error.message) {
        errMsg = error.message;
      }
      swal("Error", errMsg.slice(0, 300) || "Failed to download file", "error");
    } finally {
      setDownloading(prev => ({ ...prev, [downloadType]: false }));
    }
  };


  const renderTooltip = (text) => (
    <Tooltip id="button-tooltip">
      {text}
    </Tooltip>
  );

  return (
    <div className="d-flex align-items-center justify-content-center gap-1">
      {/* 1. Download System PDF */}
      {canDownloadSystem && (
        <OverlayTrigger placement="top" overlay={renderTooltip("Download System PDF")}>
          <Button 
            variant="outline-primary" 
            size="sm" 
            onClick={() => handleDownload("system")}
            disabled={downloading.system}
            className="p-1 px-2 shadow-none"
          >
            {downloading.system ? <Spinner animation="border" size="sm" /> : <FaFilePdf />}
          </Button>
        </OverlayTrigger>
      )}

      {/* 2. Download Verified Copy */}
      {canDownloadVerified && (
        <OverlayTrigger placement="top" overlay={renderTooltip(hasVerifiedFile ? "Download Verified PDF" : "Verified PDF Not Available")}>
          <Button 
            variant="outline-success" 
            size="sm" 
            onClick={() => handleDownload("verified")}
            disabled={!hasVerifiedFile || downloading.verified}
            className="p-1 px-2 shadow-none"
          >
            {downloading.verified ? <Spinner animation="border" size="sm" /> : <FaDownload />}
          </Button>
        </OverlayTrigger>
      )}

      {/* 3. Download Merged File */}
      {canDownloadMerged && (
        <OverlayTrigger placement="top" overlay={renderTooltip(hasVerifiedFile ? "Download Merged PDF" : "Merged PDF Not Available")}>
          <Button 
            variant="outline-danger" 
            size="sm" 
            onClick={() => handleDownload("merged")}
            disabled={!hasVerifiedFile || downloading.merged}
            className="p-1 px-2 shadow-none"
          >
            {downloading.merged ? <Spinner animation="border" size="sm" /> : <FaFileArchive />}
          </Button>
        </OverlayTrigger>
      )}

      {/* 4. Upload Option */}
      {canUploadVerified && (
        <OverlayTrigger placement="top" overlay={renderTooltip("Upload Verified PDF")}>
          <Button 
            variant="outline-secondary" 
            size="sm" 
            onClick={() => setShowUploadModal(true)}
            className="p-1 px-2 shadow-none"
          >
            <FaUpload />
          </Button>
        </OverlayTrigger>
      )}

      {/* Upload Modal */}
      <Modal show={showUploadModal} onHide={() => setShowUploadModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title className="fs-5">Manage Verified Copy</Modal.Title>
        </Modal.Header>
        <Modal.Body className="p-3">
          <div className="small text-muted mb-2">
            Upload your own signed/verified PDF here. The merged download will combine:
            system-generated PDF + your uploaded PDF.
          </div>

          {/* Current File Preview */}
          {verifiedCopy && (verifiedCopy.filePath || verifiedCopy.fileUrl || verifiedCopy.fileName) && (
            <div className="mb-3">
              <label className="form-label small fw-bold">Existing Verified Copy:</label>
              <FilePreview file={verifiedCopy} />
            </div>
          )}

          <Form.Group>
            <Form.Label className="small fw-bold">
              {hasVerifiedFile ? "Replace with New PDF:" : "Select PDF File:"}
            </Form.Label>
            <Form.Control 
              type="file" 
              accept="application/pdf" 
              onChange={handleFileChange} 
              size="sm"
            />
          </Form.Group>

          {/* New File Preview */}
          {file && (
            <div className="mt-3">
              <label className="form-label small fw-bold text-primary">New Selection:</label>
              <FilePreview file={file} onRemove={() => setFile(null)} />
            </div>
          )}
        </Modal.Body>
        <Modal.Footer className="p-2">
          <Button variant="light" size="sm" onClick={() => setShowUploadModal(false)}>
            Cancel
          </Button>
          <Button 
            variant="primary" 
            size="sm"
            onClick={handleUploadClick} 
            disabled={uploading || !file} 
          >
            {uploading ? (
              <><Spinner animation="border" size="sm" className="me-1" /> ...</>
            ) : isSignatureRequired ? (
              hasVerifiedFile ? "Proceed to Sign & Replace" : "Proceed to Sign & Upload"
            ) : (
              hasVerifiedFile ? "Replace" : "Upload"
            )}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Signature Modal (Only opens if isSignatureRequired is true) */}
      {showSignModal && (
        <SignatureModal
          show={showSignModal}
          onHide={() => setShowSignModal(false)}
          onSave={handleSaveSignature}
          title="Provide Digital Signature"
        />
      )}
    </div>
  );
};

export default VerifiedCopyActions;
